import { useTranslation } from "react-i18next";
import { ettsBootstrap } from "@/lib/bootstrap";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useSettingsCategory, useInstanceMode } from "@/lib/settings";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { Table } from "@/components/application/table/table";

/**
 * General — read-only přehled: HTTP klient nastavení (kategorie `http`) + build
 * profil / instance mode. Konfigurace se mění env proměnnými při nasazení.
 */
export function GeneralPage() {
  const { t } = useTranslation();
  const http = useSettingsCategory("http");
  const mode = useInstanceMode();

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("general.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("general.subtitle")}</SectionHeader.Subheading>
          </div>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="mb-3 text-sm font-semibold text-secondary">{t("general.runtime")}</h2>
        {mode.isLoading && <LoadingIndicator />}
        {mode.data && (
          <dl className="text-sm">
            <Row label={t("general.version")}>{ettsBootstrap().version}</Row>
            <Row label={t("tokenSettings.buildProfile")}>{mode.data.buildProfile}</Row>
            <Row label={t("tokenSettings.currentMode")}>{mode.data.currentMode}</Row>
          </dl>
        )}
      </section>

      <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
        <h2 className="mb-3 text-sm font-semibold text-secondary">{t("general.httpClient")}</h2>
        {http.isLoading && <LoadingIndicator />}
        {http.isError && <p className="text-sm text-tertiary">{t("general.noHttp")}</p>}
        {http.data && (
          <dl className="text-sm">
            <Row label={t("general.activeGlobalTls")}>
              <span className="font-mono">{String((http.data.config as Record<string, unknown> | undefined)?.tlsVersions ?? "TLSv1.2")}</span>
            </Row>
            {Object.entries(http.data.config ?? {}).map(([k, v]) => (
              <Row key={k} label={k}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</Row>
            ))}
          </dl>
        )}
        <p className="mt-3 text-xs text-tertiary">{t("general.envNote")}</p>
        <p className="mt-1 text-xs text-tertiary">{t("general.poolMonitorNote")}</p>
      </section>

      <EnvRefTable
        title={t("general.envReference")}
        rows={[
          ["HTTP_POOL_MAX_TOTAL", "200", t("general.env.poolMaxTotal")],
          ["HTTP_POOL_MAX_PER_ROUTE", "50", t("general.env.poolMaxPerRoute")],
          ["HTTP_CONNECT_TIMEOUT", "5000", t("general.env.connectTimeout")],
          ["HTTP_READ_TIMEOUT", "30000", t("general.env.readTimeout")],
          ["HTTP_TLS_VERSIONS", "TLSv1.2", t("general.env.tlsVersions")],
        ]}
        example={`# .env override\nHTTP_POOL_MAX_TOTAL=400\nHTTP_READ_TIMEOUT=60000`}
      />
    </div>
  );
}

/** Statická referenční tabulka env proměnných (name / default / účel) + .env příklad. */
export function EnvRefTable({ title, rows, example }: { title: string; rows: [string, string, string][]; example?: string }) {
  return (
    <section className="rounded-xl bg-primary p-5 ring-1 ring-secondary">
      <h2 className="mb-3 text-sm font-semibold text-secondary">{title}</h2>
      <div className="overflow-hidden rounded-lg ring-1 ring-secondary">
        <Table aria-label={title} selectionMode="none" size="sm">
          <Table.Header>
            <Table.Head isRowHeader id="name" label="Env" />
            <Table.Head id="def" label="Default" />
            <Table.Head id="desc" label="Popis" />
          </Table.Header>
          <Table.Body items={rows.map(([name, def, desc]) => ({ id: name, name, def, desc }))}>
            {(r) => (
              <Table.Row id={r.id}>
                <Table.Cell className="font-mono text-xs text-secondary">{r.name}</Table.Cell>
                <Table.Cell className="font-mono text-xs text-tertiary">{r.def}</Table.Cell>
                <Table.Cell className="text-tertiary">{r.desc}</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      {example && (
        <pre className="mt-3 overflow-x-auto rounded-md bg-secondary p-3 font-mono text-xs text-secondary ring-1 ring-secondary">{example}</pre>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-secondary py-2 sm:grid-cols-[240px_1fr] sm:gap-4">
      <dt className="text-tertiary">{label}</dt>
      <dd className="break-words text-primary">{children}</dd>
    </div>
  );
}
